import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import type { TabType } from 'app/components/ui/nav-tabs'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { Location } from 'app/generated/graphql'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import { useLocationStore } from 'app/stores/StoreContext'
import { gqlMutation } from 'app/lib/graphqlClient'
import { graphql } from 'app/generated/gql'

const SET_AGENT_LIVE_MUTATION = graphql(`
  mutation AIAgentLocationSetLive($id: ID!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      id
      isAIAgentLive
    }
  }
`)

function getTabs(products?: string[] | null): TabType[] {
  const tabs: TabType[] = [{ name: `Voice`, href: `configuration` }]
  if (products?.includes(`SMS Agent`)) {
    tabs.push({ name: `SMS`, href: `sms` })
  }
  return tabs
}

export type UseAIAgentLocationReturn = {
  locationName: string
  tabs: TabType[]
  isAgentLive: boolean
  testPhoneNumber: string | undefined
  onToggleAgentLive: (value: boolean) => Promise<boolean>
}

export function useAIAgentLocation(): UseAIAgentLocationReturn {
  const { locationId } = useParams()
  const store = useLocationStore()
  const [confirm] = useConfirmation()

  useEffect(() => {
    if (!locationId) return
    store.fetchOne(locationId)
  }, [locationId])

  const location = store.current as Location | undefined

  const onToggleAgentLive = async (value: boolean) => {
    return confirm({
      title: `Change AI Agent Live Status`,
      description: value
        ? `Are you sure you want to turn on the AI Agent?`
        : `Are you sure you want to disable the AI Agent? Calls will be routed directly to the office.`,
      variant: `danger`,
    })
      .then(async () => {
        if (!locationId) return false
        const result = await gqlMutation(SET_AGENT_LIVE_MUTATION, { id: locationId, input: { isAIAgentLive: value } })
        if (!result.error && result.data?.updateLocation) {
          notifySuccess(`AI Agent status updated`)
          store.updateInList(locationId!, { isAIAgentLive: value } as Partial<Location>)
        } else {
          notifyError(`AI Agent live status could not be updated`)
        }
        return !!result.data?.updateLocation
      })
      .catch(() => false)
  }

  return {
    locationName: location?.name ?? ``,
    tabs: getTabs(location?.products),
    isAgentLive: !!location?.isAIAgentLive,
    testPhoneNumber: formatPhoneNumber(location?.twilioNumbers?.find((n) => n.type === `ai-testing`)?.phone),
    onToggleAgentLive,
  }
}
